import { useCallback, useState } from 'react';
import dsnAxios from 'services/axios';
import apolloClient from 'services/apolloClient';
import { UPSERT_WORKSPACE_MUTATION } from 'data/queries';

export const LIVEDEMOS_STATUS = {
  IDLE: 'idle',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  ERROR: 'error',
  ABORTED: 'aborted'
};

// Returns true if the workspace is still accessible on the platform, false if it
// is definitively gone, and null when we can't tell (transient error / bad shape).
async function livedemosWorkspaceExists(livedemosWorkspaceId) {
  try {
    const { data } = await dsnAxios.get('demopilot/projects');
    if (!Array.isArray(data)) return null;
    return data.some((p) => p?.id === livedemosWorkspaceId);
  } catch {
    return null;
  }
}

export const useLivedemosStep = ({ enabled, title, drNumber, presetName }) => {
  const [status, setStatus] = useState(LIVEDEMOS_STATUS.IDLE);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [error, setError] = useState(null);
  const [reusedExistingWorkspace, setReusedExistingWorkspace] = useState(false);

  const reset = useCallback(() => {
    setStatus(LIVEDEMOS_STATUS.IDLE);
    setWorkspaceId(null);
    setError(null);
    setReusedExistingWorkspace(false);
  }, []);

  const run = useCallback(
    async (dsnWorkspaceId, existingLivedemosWorkspaceId) => {
      if (!dsnWorkspaceId) {
        setError('Could not determine DSN workspace ID');
        setStatus(LIVEDEMOS_STATUS.ERROR);
        return;
      }
      setStatus(LIVEDEMOS_STATUS.IN_PROGRESS);
      try {
        // If the DSN workspace is already linked to a livedemos workspace,
        // reuse it: skip create + skip upsert, only register the new demo.
        let wsId = existingLivedemosWorkspaceId || null;
        let reused = !!existingLivedemosWorkspaceId;

        // A stored livedemosWorkspaceId can be stale (workspace deleted on the
        // platform); registering a demo against it would fail, so when it is
        // confirmed gone, fall back to creating and linking a fresh workspace.
        if (reused && (await livedemosWorkspaceExists(existingLivedemosWorkspaceId)) === false) {
          wsId = null;
          reused = false;
        }

        if (!reused) {
          const { data: workspace } = await dsnAxios.post(
            'demopilot/projects',
            {
              name: title,
              drNumber
            }
          );
          wsId = workspace?.id;
          if (!wsId)
            throw new Error('Livedemos workspace creation returned no ID');
        }

        await dsnAxios.post(`demopilot/projects/${wsId}/demos`, {
          name: title,
          templateId: presetName,
          metadata: { dsnWorkspaceId }
        });

        if (!reused) {
          // save livedemosWorkspaceId to DSN db - workspaces table
          await apolloClient.mutate({
            mutation: UPSERT_WORKSPACE_MUTATION,
            variables: { id: dsnWorkspaceId, livedemosWorkspaceId: wsId }
          });
        }

        setReusedExistingWorkspace(reused);
        setWorkspaceId(wsId);
        setStatus(LIVEDEMOS_STATUS.SUCCESS);
      } catch (err) {
        console.error('Failed to setup livedemos workspace:', err);
        setError(
          err?.response?.data?.message || err?.message || 'Unknown error'
        );
        setStatus(LIVEDEMOS_STATUS.ERROR);
      }
    },
    [title, drNumber, presetName]
  );

  const abort = useCallback(() => {
    setStatus(LIVEDEMOS_STATUS.ABORTED);
  }, []);

  const isSettled =
    status === LIVEDEMOS_STATUS.SUCCESS ||
    status === LIVEDEMOS_STATUS.ERROR ||
    status === LIVEDEMOS_STATUS.ABORTED;

  return {
    enabled,
    status,
    workspaceId,
    error,
    reusedExistingWorkspace,
    run,
    reset,
    abort,
    isSettled
  };
};
